/**
 * Sales Team
 *
 * What each rep sold in a window: new sales and their dollars, the links they
 * sent and how many closed, and their customers' renewals beside it.
 *
 * A sale is a NEW charge on the sales_team register credited to the rep
 * stamped on the patient — the same charges as the Daily Report's Sales Team
 * column, so the Team row here matches it for the same dates. See
 * backend services/salesTeamReport.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import { Loader2, RefreshCw, AlertCircle, PhoneCall, Package, Route, X } from 'lucide-react'

import { salesTeamAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { formatMoney, formatCount } from '../components/DailyReportTable'

const DRUG_LABELS = { sema: 'Semaglutide', tirz: 'Tirzepatide' }

function formatRate(value) {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(1)}%`
}

function formatWhen(ms) {
  if (!ms) return '—'
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function getDefaultDateRange() {
  return { from: subDays(new Date(), 29), to: new Date() }
}

function Kpi({ label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  )
}

export default function SalesTeamPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // The selected rep, in the URL so "Grace's sales this month" is a link.
  const selectedRep = searchParams.get('rep') || ''

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (!newRange?.from || !newRange?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(newRange.from, 'yyyy-MM-dd'))
      params.set('end', format(newRange.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const selectRep = useCallback((key) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (key) params.set('rep', key)
      else params.delete('rep')
      return params
    })
  }, [setSearchParams])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await salesTeamAPI.get({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      })
      setData(response.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  const reps = data?.reps || []
  const totals = data?.totals
  const repByKey = useMemo(() => new Map(reps.map(rep => [rep.key, rep])), [reps])
  const sales = useMemo(
    () => (data?.sales || []).filter(sale => !selectedRep || sale.rep === selectedRep),
    [data, selectedRep]
  )
  const selected = repByKey.get(selectedRep)

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Sales Team</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            New sales on the sales team price book, by the rep who sent the link
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8 sm:h-9 sm:w-9">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data?.unassigned?.sales > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {formatCount(data.unassigned.sales)} rep sale{data.unassigned.sales === 1 ? '' : 's'} ({formatMoney(data.unassigned.revenue)})
            could not be matched to a rep and are not in the table below.
          </span>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : totals ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi
              label="Sales"
              value={formatCount(totals.sales)}
              hint={totals.reddit_sales ? `new customers, ${formatCount(totals.reddit_sales)} via Reddit` : 'new customers'}
            />
            <Kpi
              label="Revenue"
              value={formatMoney(totals.revenue)}
              hint={totals.reddit_revenue ? `${formatMoney(totals.reddit_revenue)} via Reddit` : 'from those sales'}
            />
            <Kpi label="AOV" value={formatMoney(totals.aov)} />
            <Kpi
              label="Close rate"
              value={formatRate(totals.close_rate)}
              hint={data.links_available ? `${formatCount(totals.links)} links sent` : 'links unavailable'}
            />
            <Kpi
              label="Renewals"
              value={formatMoney(totals.renewal_revenue)}
              hint={`${formatCount(totals.renewals)} from reps' customers`}
            />
          </div>

          <Card>
            <CardContent className="p-0">
              {reps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <PhoneCall className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">No rep sales or links in this range.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className="px-4 py-2 font-medium">Rep</th>
                        <th className="px-4 py-2 font-medium text-right">Sales</th>
                        <th className="px-4 py-2 font-medium text-right">Revenue</th>
                        <th className="px-4 py-2 font-medium text-right whitespace-nowrap">% of team</th>
                        <th className="px-4 py-2 font-medium text-right">AOV</th>
                        <th className="px-4 py-2 font-medium text-right whitespace-nowrap">Sema / Tirz</th>
                        <th className="px-4 py-2 font-medium text-right">Links</th>
                        <th className="px-4 py-2 font-medium text-right whitespace-nowrap">Close rate</th>
                        <th className="px-4 py-2 font-medium text-right">Renewals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reps.map(rep => (
                        <tr
                          key={rep.key}
                          onClick={() => selectRep(rep.key === selectedRep ? '' : rep.key)}
                          className={`border-b cursor-pointer hover:bg-muted/40 ${rep.key === selectedRep ? 'bg-muted/60' : ''}`}
                        >
                          <td className="px-4 py-2.5">
                            <span className="font-medium">{rep.name}</span>
                            <span className="block text-[11px] text-muted-foreground">{rep.email || rep.key}</span>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">
                            {formatCount(rep.sales)}
                            {rep.reddit_sales > 0 && <span className="block text-[11px] font-normal text-muted-foreground">{formatCount(rep.reddit_sales)} via Reddit</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">
                            {formatMoney(rep.revenue)}
                            {rep.reddit_revenue > 0 && <span className="block text-[11px] font-normal text-muted-foreground">{formatMoney(rep.reddit_revenue)} via Reddit</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {totals.revenue ? formatRate(rep.revenue / totals.revenue) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(rep.aov)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {formatCount(rep.sema_sales)} / {formatCount(rep.tirz_sales)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {data.links_available ? formatCount(rep.links) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                            {formatRate(rep.close_rate)}
                            {rep.close_rate_basis === 'reddit_cvr' && (
                              <span
                                className="block text-[11px] text-muted-foreground"
                                title={`Daily Report Reddit CVR: ${formatCount(rep.register_cvr_sales)} Reddit sales / ${formatCount(rep.register_cvr_leads)} Reddit leads`}
                              >
                                Reddit CVR{rep.link_close_rate != null ? ` · links ${formatRate(rep.link_close_rate)}` : ''}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                            {formatMoney(rep.renewal_revenue)}
                            <span className="block text-[11px] text-muted-foreground">{formatCount(rep.renewals)} charges</span>
                          </td>
                        </tr>
                      ))}
                      <tr className="font-semibold bg-muted/30">
                        <td className="px-4 py-2.5">Team</td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          {formatCount(totals.sales)}
                          {totals.reddit_sales > 0 && <span className="block text-[11px] font-normal text-muted-foreground">{formatCount(totals.reddit_sales)} via Reddit</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          {formatMoney(totals.revenue)}
                          {totals.reddit_revenue > 0 && <span className="block text-[11px] font-normal text-muted-foreground">{formatMoney(totals.reddit_revenue)} via Reddit</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{totals.revenue ? '100%' : '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(totals.aov)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {formatCount(totals.sema_sales)} / {formatCount(totals.tirz_sales)}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {data.links_available ? formatCount(totals.links) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatRate(totals.close_rate)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(totals.renewal_revenue)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">
              {selected ? `${selected.name}'s sales` : 'Every sale'}
              <span className="ml-2 font-normal text-muted-foreground">{formatCount(sales.length)}</span>
            </h2>
            {selected && (
              <Button variant="outline" size="sm" onClick={() => selectRep('')}>
                <X className="h-3.5 w-3.5 mr-1" /> All reps
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {sales.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No sales in this range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className="px-4 py-2 font-medium whitespace-nowrap">When</th>
                        <th className="px-4 py-2 font-medium">Rep</th>
                        <th className="px-4 py-2 font-medium">Customer</th>
                        <th className="px-4 py-2 font-medium">Product</th>
                        <th className="px-4 py-2 font-medium text-right">Paid</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map(sale => (
                        <tr key={sale.charge_id || `${sale.email}-${sale.charged_at}`} className="border-b last:border-0 hover:bg-muted/40">
                          <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{formatWhen(sale.charged_at)}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span className="flex items-center gap-1.5">
                              {repByKey.get(sale.rep)?.name || '—'}
                              {sale.register === 'reddit' && (
                                <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:text-orange-300">Reddit</span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2.5"><span className="select-all">{sale.email || '—'}</span></td>
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-1.5">
                              {DRUG_LABELS[sale.drug] || 'Other'}
                              {sale.bundle_fills && (
                                <span
                                  title="Prepaid bundle — charged once up front, shipped over several months"
                                  className="inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300"
                                >
                                  <Package className="h-2.5 w-2.5" />
                                  {sale.bundle_fills}-fill bundle
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">{formatMoney(sale.amount)}</td>
                          <td className="px-4 py-2.5 text-right">
                            {sale.email && (
                              <Link
                                to={`/reports/user-journey?q=${encodeURIComponent(sale.email)}`}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                              >
                                Journey <Route className="h-3 w-3" />
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            A sale is a new customer's first charge on the sales team price book, credited to the rep
            on the patient, or on the Reddit price book, credited to whoever runs Reddit (Sal at
            Science) — so the Team row is the Daily Report's Sales Team plus Reddit columns. Reddit
            sales had no link sent, so the Reddit owner's close rate is the Daily Report's Reddit CVR
            (Reddit attributed sales over Reddit leads) and the Team close rate counts link sales only. A prepaid bundle
            is one sale at the price paid. Win-back links from Customer.io use the same price book but
            have no rep, so they credit nobody here. Renewals are later charges from a rep's customers,
            shown beside sales and never added to them. Close rate is sales over links sent in the same
            dates, not a cohort.
            {data.quick_refunds_checked === false && ' Refunds could not be checked for this range.'}
          </p>
        </>
      ) : null}
    </div>
  )
}
